// src/stores/websocketStore.js
// Creator Vue3 BFF Proxy Backend

import { defineStore } from 'pinia';

// Choose the correct HTTP base to derive WS URL from
let WS_HTTP_BASE;

if (process.env.NODE_ENV === 'development') {
  // In dev, talk directly to the backend at :3000
  WS_HTTP_BASE =
    process.env.VUE_APP_PLATFORMA_API_BACKEND_URL || 'http://localhost:3000';
} else {
  // In prod, prefer an explicit backend URL if set; otherwise fall back to proxy
  WS_HTTP_BASE =
    process.env.VUE_APP_PLATFORMA_API_BACKEND_URL ||
    process.env.VUE_APP_API_PROXY_URL ||
    null;
}

// Turn http/https into ws/wss
const WS_URL = WS_HTTP_BASE
  ? WS_HTTP_BASE.replace(/^http/i, 'ws') // http -> ws, https -> wss
  : null;
const CREATOR_API_PROXY_BASE = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const CREATOR_SESSION_URL = `${CREATOR_API_PROXY_BASE}/api/auth/validate-session`;
const CREATOR_WEBSOCKET_TICKET_URL = `${CREATOR_API_PROXY_BASE}/api/auth/websocket-ticket`;

const buildAuthenticatedWebSocketUrl = (ticket) => {
  const url = new URL(WS_URL);
  url.searchParams.set('client', 'creator');
  url.searchParams.set('ticket', String(ticket || ''));
  return url.toString();
};

export const useWebsocketStore = defineStore('websocket', {
  state: () => ({
    socket: null,
    isConnected: false,
    reconnectDelay: 3000,
    listeners: {},
    heartbeatInterval: null,
    reconnectTimer: null,
    isConnecting: false,
    memberPresenceIds: [],
    hasMemberPresenceSubscription: false,
    // 🔥 Controls whether the store will auto-reconnect
    shouldReconnect: true,   
  }),

  actions: {
    async connect() {
      if (!WS_URL) {
        console.warn('[WS] WS_URL is not set. Skipping WebSocket connection.');
        return;
      }

      // If someone turned off reconnect (logout), re-enable it when we
      // explicitly call connect again from an authenticated shell
      if (!this.shouldReconnect) {
        this.shouldReconnect = true;
      }


      if (
        this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      if (this.isConnecting) return;
      this.isConnecting = true;
      let websocketTicket = '';
      try {
        const sessionResponse = await fetch(CREATOR_SESSION_URL, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!sessionResponse.ok) {
          if ([401, 403].includes(Number(sessionResponse.status))) {
            this.shouldReconnect = false;
            console.warn('[WS] Creator session is not authenticated; WebSocket connection skipped.');
          } else {
            console.warn(`[WS] Creator session preflight returned ${sessionResponse.status}; retrying.`);
            this.scheduleReconnect();
          }
          return;
        }

        const ticketResponse = await fetch(CREATOR_WEBSOCKET_TICKET_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const ticketPayload = await ticketResponse.json().catch(() => ({}));
        websocketTicket = String(ticketPayload?.ticket || '').trim();
        if (!ticketResponse.ok || !websocketTicket) {
          if ([401, 403].includes(Number(ticketResponse.status))) {
            this.shouldReconnect = false;
            console.warn('[WS] Creator live connection ticket was rejected; reconnect disabled.');
          } else {
            console.warn(`[WS] Creator live connection ticket returned ${ticketResponse.status}; retrying.`);
            this.scheduleReconnect();
          }
          return;
        }
      } catch (error) {
        console.warn('[WS] Creator live connection preparation failed; retrying.', error);
        this.scheduleReconnect();
        return;
      } finally {
        this.isConnecting = false;
      }

      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      const authenticatedWsUrl = buildAuthenticatedWebSocketUrl(websocketTicket);
      console.log('[WS] Connecting to authenticated creator WebSocket endpoint.');
      const ws = new WebSocket(authenticatedWsUrl);
      this.socket = ws;

      ws.addEventListener('open', () => {
        console.log('[WS] Connected');
        this.isConnected = true;

        const helloPayload = {
          type: 'creator',
          event: 'hello',
          ts: Date.now(),
        };

        try {
          ws.send(JSON.stringify(helloPayload));
        } catch (err) {
          console.error('[WS] Failed to send hello payload', err);
        }

        // Presence subscriptions are page-scoped, but the socket can connect
        // or reconnect after a page initially requested them. Reapply the most
        // recent member set so online indicators always receive a snapshot.
        this.flushMemberPresenceSubscription();

        // 🔥 Start creator heartbeat once the socket is open
        this.startHeartbeat();
      });

      ws.addEventListener('message', (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          console.warn('[WS] Non-JSON message from server:', event.data);
          return;
        }

        const type = data.type;
        const payload = data.payload;

        if (!type) return;

        if (type === 'presence:memberUpdate') {
          console.log('[WS][frontend] presence:memberUpdate', data);
        }

        // (Optional: debug creator heartbeat replies)
        if (type === 'creator_heartbeat') {
          console.log('[WS][frontend] creator_heartbeat', data);
        }

        const set = this.listeners[type];
        if (set) {
          set.forEach((handler) => {
            try {
              handler(payload, data);
            } catch (e) {
              console.error('[WS] Listener error for type', type, e);
            }
          });
        }
      });

      ws.addEventListener('close', (event) => {
        console.log(
          `[WS] Disconnected (code=${event.code}, reason=${event.reason || 'none'})`
        );
        this.isConnected = false;
        this.socket = null;

        if ([4001, 4002, 4003].includes(Number(event.code))) {
          this.stopHeartbeat();
          this.shouldReconnect = false;
          console.warn('[WS] Authentication rejected; automatic reconnect disabled.');
          return;
        }

        // 🔥 Stop heartbeat on close
        this.stopHeartbeat();

        // 🔥 Respect shouldReconnect flag
        if (!this.shouldReconnect) {
          console.log('[WS] Auto-reconnect disabled, not reconnecting.');
          return;
        }

        this.scheduleReconnect();
      });

      ws.addEventListener('error', (err) => {
        console.error('[WS] Error', err);

        // 🔥 Stop heartbeat on error
        this.stopHeartbeat();

        try {
          ws.close();
        } catch (closeErr) {
          console.warn(
            '[WS] Error while closing socket after error',
            closeErr
          );
        }
      });
    },

    scheduleReconnect() {
      if (!this.shouldReconnect || this.reconnectTimer) return;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        console.log('[WS] Reconnecting…');
        void this.connect();
      }, this.reconnectDelay);
    },

    
    // 🔥 Creator heartbeat loop
    startHeartbeat() {
      // Clear any existing interval
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      if (!this.socket) {
        return;
      }

      this.heartbeatInterval = setInterval(() => {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          return;
        }

        const payload = {
          type: 'creator',
          event: 'heartbeat',
          ts: Date.now(),
        };

        console.log('[WS] -> sending creator heartbeat', payload);

        try {
          this.socket.send(JSON.stringify(payload));
        } catch (err) {
          console.error('[WS] Failed to send creator heartbeat', err);
        }
      }, 15000); // 15s, safely under the 45s TTL
    },

    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    },

    // 🔥 NEW: clean disconnect specifically for logout
    disconnectForLogout() {
      console.log('[WS] disconnectForLogout: disabling auto-reconnect and closing socket');
      this.shouldReconnect = false;

      this.stopHeartbeat();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      if (this.socket) {
        try {
          if (this.socket.readyState === WebSocket.OPEN) {
            this.socket.close(1000, 'LOGOUT');
          } else {
            this.socket.close();
          }
        } catch (err) {
          console.error('[WS] Error closing socket in disconnectForLogout', err);
        }
      }

      this.socket = null;
      this.isConnected = false;
      this.memberPresenceIds = [];
      this.hasMemberPresenceSubscription = false;
    },

    captureMemberPresenceSubscription(obj) {
      if (
        obj?.type !== 'creator'
        || obj?.event !== 'presence:subscribeMembers'
        || !Array.isArray(obj?.payload?.memberIds)
      ) {
        return false;
      }

      this.memberPresenceIds = Array.from(new Set(
        obj.payload.memberIds
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      ));
      this.hasMemberPresenceSubscription = true;
      return true;
    },

    flushMemberPresenceSubscription() {
      if (
        !this.hasMemberPresenceSubscription
        || !this.socket
        || this.socket.readyState !== WebSocket.OPEN
      ) {
        return false;
      }

      try {
        this.socket.send(JSON.stringify({
          type: 'creator',
          event: 'presence:subscribeMembers',
          payload: { memberIds: [...this.memberPresenceIds] },
          ts: Date.now(),
        }));
        return true;
      } catch (error) {
        console.warn('[WS] Failed to restore member presence subscription', error);
        return false;
      }
    },

    send(obj) {
      this.captureMemberPresenceSubscription(obj);
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.warn('[WS] Cannot send, socket not open', obj);
        return false;
      }
      this.socket.send(JSON.stringify(obj));
      return true;
    },

    subscribe(type, handler) {
      if (!this.listeners[type]) {
        this.listeners[type] = new Set();
      }
      this.listeners[type].add(handler);
      return () => this.unsubscribe(type, handler);
    },

    unsubscribe(type, handler) {
      const set = this.listeners[type];
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) delete this.listeners[type];
    },
  },
});

